import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {APIListWrapper} from "../../models/common/api-list-wrapper.model";
import {BankAccount} from "./bank-account.model";
import {Bank} from "./bank.model";

@Injectable({
    providedIn: 'root'
})
export class BankAccountService {
    private baseUrl = `${environment.restApiUrl}bank-accounts`;

    constructor(private http: HttpClient) { }

    /**
     * Fetches a paginated list of bank accounts for a specific owner.
     * @param ownerType The type of the entity that owns the accounts (e.g., 'COMPANY').
     * @param ownerId The ID of the owner.
     * @param searchText Optional text to search across account fields.
     * @param page The page number to retrieve (0-indexed).
     * @param size The number of items per page.
     * @returns An Observable of a paginated list of bank accounts.
     */
    getBankAccounts(
        ownerType: string,
        ownerId: number,
        searchText: string = '',
        page: number = 0,
        size: number = 10
    ): Observable<APIListWrapper<BankAccount>> {
        let params = new HttpParams()
            .set('ownerType', ownerType)
            .set('ownerId', ownerId.toString())
            .set('page', page.toString())
            .set('size', size.toString());

        if (searchText) {
            params = params.append('searchText', searchText);
        }

        return this.http.get<APIListWrapper<BankAccount>>(this.baseUrl, { params });
    }

    /**
     * Retrieves a single bank account by its unique ID.
     * @param id The ID of the bank account.
     * @returns An Observable of the bank account.
     */
    getBankAccountById(id: number): Observable<BankAccount> {
        const url = `${this.baseUrl}/${id}`;
        return this.http.get<BankAccount>(url);
    }

    /**
     * Creates a new bank account.
     * @param bankAccount The bank account data to create. Using a DTO is recommended.
     * @returns An Observable of the newly created bank account.
     */
    createBankAccount(bankAccount: BankAccount): Observable<BankAccount> {
        return this.http.post<BankAccount>(this.baseUrl, bankAccount);
    }

    /**
     * Updates an existing bank account.
     * @param id The ID of the bank account to update.
     * @param bankAccount The updated bank account data.
     * @returns An Observable of the updated bank account.
     */
    updateBankAccount(id: number, bankAccount: BankAccount): Observable<BankAccount> {
        const url = `${this.baseUrl}/${id}`;
        return this.http.put<BankAccount>(url, bankAccount);
    }

    /**
     * Deletes a bank account by its unique ID.
     * @param id The ID of the bank account to delete.
     * @returns An Observable of void, completing when the operation is successful.
     */
    deleteBankAccount(id: number): Observable<void> {
        const url = `${this.baseUrl}/${id}`;
        return this.http.delete<void>(url);
    }

    /**
     * Fetches a list of all banks associated with a specific country.
     * @param countryId The ID of the country.
     * @returns An Observable of a list of banks.
     */
    getBanksByCountryId(countryId: number): Observable<Bank[]> {
        const url = `${this.baseUrl}/country/${countryId}`;
        return this.http.get<Bank[]>(url);
    }
}
